/* External */
import window from 'global';

/* Utils */
import importScript from '../../importScript';
import { Routes, getLink } from '../../router';
import { ConnectionType } from '../../constants';
import { closeApp } from '../../functions';

/* Constants */
const APPLICATION_MANAGER_SERVICE = 'luna://com.webos.applicationManager';
const TV_APP_ID = 'com.webos.app.livetv';
const WEBOS_SCRIPT = '/webOSTVjs-1.2.3/webOSTV.js';
const WEBOS_DEV_SCRIPT = '/webOSTVjs-1.2.3/webOSTV-dev.js';
const RELAUNCH_EVENT = 'webOSRelaunch';

let webOSApiPromise;
let webOSDevApiPromise;

export async function getWebOSApi() {
  if (!webOSApiPromise) {
    webOSApiPromise = importScript(WEBOS_SCRIPT);
  }
  await webOSApiPromise;
  return window.webOS;
}

export async function getWebOSDevApi() {
  if (!webOSDevApiPromise) {
    webOSDevApiPromise = importScript(WEBOS_DEV_SCRIPT);
  }
  await webOSDevApiPromise;
  return window.webOSDev;
}

export async function getAppId() {
  const webosApi = await getWebOSApi();
  return webosApi.fetchAppId();
}

export function isWebOS() {
  return !!window.PalmSystem;
}

function promesifyFunctionWithCallback({
  func,
  timeout,
  defaultValue = undefined,
  processCallbackParams = (param) => param,
}) {
  if (!func) return async () => defaultValue;

  return async (...params) => (
    new Promise((resolve) => {
      let timeoutId;
      let resolved;
      if (timeout) {
        timeoutId = setTimeout(() => {
          if (!resolved) {
            resolve(defaultValue);
            resolved = true;
          }
        }, timeout);
      }
      func((...callbackReturn) => {
        if (!resolved) {
          resolve(processCallbackParams(...callbackReturn));
          resolved = true;
          if (timeoutId) clearTimeout(timeoutId);
        }
      }, ...params);
    })
  );
}

let deviceInfoPromise;
const getDeviceInfoFunction = promesifyFunctionWithCallback({
  func: async (...params) => {
    const webOS = await getWebOSApi();
    return webOS.deviceInfo(...params);
  },
});
export function getDeviceInfo() {
  deviceInfoPromise = deviceInfoPromise || getDeviceInfoFunction();

  return deviceInfoPromise;
}

export async function getSystemInfo() {
  const webOS = await getWebOSApi();
  return webOS.systemInfo();
}

export async function getTimezone() {
  const systemInfo = await getSystemInfo();
  return systemInfo?.timezone;
}

export async function sendAppToBackground() {
  const webOS = (await getWebOSApi());
  if (webOS) {
    webOS.service.request(APPLICATION_MANAGER_SERVICE, {
      method: 'launch',
      parameters: { id: TV_APP_ID },
      onSuccess(response) {
        if (response.returnValue === false) {
          console.error(`Error sending Application to background and bringing TV Application with ID ${TV_APP_ID} to the foreground.`);
          closeApp();
        }
      },
      onFailure(error) {
        console.error('webOSUtils>sendAppToBackground', error);
        closeApp();
      },
    });
  } else {
    closeApp();
  }
}

/* Close + relaunch this app as a FRESH process — the only reset that clears
   the WAM's degraded MSE media pipeline on webOS 3 (verified 2026-08-17: a
   source reload / MediaSource recreation does NOT recover it; a fresh app
   process does). The relaunch params flow back through getUrlToOpen →
   App.jsx navigation (pass { url } to land on the same lego channel). */
export async function relaunchApp(params) {
  const webOS = (await getWebOSApi());
  const appId = await getAppId();
  if (!webOS || !appId) {
    closeApp();
    return;
  }
  webOS.service.request(APPLICATION_MANAGER_SERVICE, {
    method: 'close',
    parameters: { id: appId },
    onFailure: () => {},
  });
  /* Fire the launch slightly after the close so it lands on the fresh
     process, not the closing one. */
  setTimeout(() => {
    webOS.service.request(APPLICATION_MANAGER_SERVICE, {
      method: 'launch',
      parameters: { id: appId, params },
      onFailure: () => {},
    });
  }, 300);
}

export function addApis() {
  getWebOSApi();
  getWebOSDevApi();
}

export async function getConnectionStatus({
  subscribe,
  onSuccess,
  onFailure,
}) {
  const webOSDev = await getWebOSDevApi();
  webOSDev.connection.getStatus({
    subscribe,
    onSuccess: ({
      isInternetConnectionAvailable,
      wifi,
      wired,
    }) => {
      let connectionType;
      if (wired?.state === 'connected') connectionType = ConnectionType.ETHERNET;
      else if (wifi?.state === 'connected') connectionType = ConnectionType.WIFI;

      onSuccess({
        isConnected: isInternetConnectionAvailable,
        connectionType,
      });
    },
    onFailure,
  });
}

export async function getUrlToOpen() {
  const webOSDev = await getWebOSDevApi();
  let launchParams = webOSDev.launchParams();
  if (launchParams) {
    if (typeof launchParams === 'string') {
      launchParams = JSON.parse(launchParams);
    }

    if (launchParams.contentTarget) {
      return `directvgo:${getLink(Routes.Private.Player.Vod, { contentId: launchParams.contentTarget })}`;
    }

    return launchParams.url || undefined;
  }
  return undefined;
}

export function addRelaunchEventListener(callback) {
  const handleEvent = async () => {
    const url = await getUrlToOpen();
    window.PalmSystem.activate(); // Send app to the foreground.
    callback(url);
  };
  document.addEventListener(RELAUNCH_EVENT, handleEvent, true);
  return () => {
    document.removeEventListener(RELAUNCH_EVENT, handleEvent, true);
  };
}
